import { RouteObject } from 'react-router-dom'
import React, { lazy } from 'react'
import { Navigate } from 'react-router-dom'

import Loadable from '../components/common/Loadable'

import * as AuthUtils from '../utils/auth'
import { RootRoutes } from '../utils/routeGenerator'
import { useUserContext } from '../contexts/UserContext'

import TransferFuel from '../components/Fuel/TransferFuel'

const MeterReading = Loadable(lazy(() => import('../components/MeterReading')))
const AddMeterReading = Loadable(
  lazy(() => import('../components/MeterReading/AddReading'))
)
const Fuel = Loadable(lazy(() => import('../components/Fuel')))
const AddFuel = Loadable(lazy(() => import('../components/Fuel/AddFuel')))

const Attendance = Loadable(lazy(() => import('../components/Attendance')))
const RotationChecklist = Loadable(
  lazy(() => import('../components/Attendance/RotationChecklist'))
)
const AddAttendance = Loadable(
  lazy(() => import('../components/Attendance/AddAttendance'))
)
const Maintenance = Loadable(lazy(() => import('../components/Maintenance')))
const AddMaintenance = Loadable(
  lazy(() => import('../components/Maintenance/AddMaintenance'))
)
const MaintenanceDetails = Loadable(
  lazy(() => import('../components/Maintenance/MaintenanceDetails'))
)

const Task = Loadable(lazy(() => import('../components/Task')))

const CreateTask = Loadable(lazy(() => import('../components/Task/CreateTask')))

const CashflowHome = Loadable(lazy(() => import('../components/Cashflow')))
const AddExpense = Loadable(
  lazy(() => import('../components/Cashflow/AddExpense'))
)
const ReturnFund = Loadable(
  lazy(() => import('../components/Cashflow/ReturnFund'))
)
const CashflowExpenses = Loadable(
  lazy(() => import('../components/Cashflow/Expenses'))
)
const CashflowReturns = Loadable(
  lazy(() => import('../components/Cashflow/Returns'))
)

const ModuleGuard = ({
  moduleKey,
  children,
}: {
  moduleKey: string
  children: JSX.Element
}) => {
  const { company } = useUserContext()
  if ((company as any)?.modules?.[moduleKey] === false) {
    const NoModuleAccess = Loadable(
      lazy(() => import('../components/common/NoModuleAccess'))
    )
    return <NoModuleAccess />
  }
  return children
}

const SuspendedScreen = Loadable(
  lazy(() => import('../components/common/SuspendedScreen'))
)

const PrivateRoute = ({ children }: { children: JSX.Element }) => {
  const { company } = useUserContext()

  if (!AuthUtils.isAuthCookiePresent()) {
    return <Navigate to={RootRoutes.login()} />
  }

  // Every API call 403s while the company is suspended, so without this the
  // supervisor just sees empty screens and rings support. There is no bills page
  // in field-web to send them to — paying is the office's job.
  if (company?.status === 'SUSPENDED') {
    return <SuspendedScreen />
  }

  return children
}

const MainRoutes: RouteObject[] = [
  {
    path: '/',
    element: (
      <PrivateRoute>
        <MeterReading />
      </PrivateRoute>
    ),
  },
  {
    path: '/reading',
    element: (
      <PrivateRoute>
        <ModuleGuard moduleKey="readings">
          <MeterReading />
        </ModuleGuard>
      </PrivateRoute>
    ),
  },
  {
    path: '/reading/add',
    element: (
      <PrivateRoute>
        <ModuleGuard moduleKey="readings">
          <AddMeterReading />
        </ModuleGuard>
      </PrivateRoute>
    ),
  },
  {
    path: '/attendances',
    element: (
      <PrivateRoute>
        <ModuleGuard moduleKey="attendance">
          <Attendance />
        </ModuleGuard>
      </PrivateRoute>
    ),
  },
  {
    path: '/attendances/add',
    element: (
      <PrivateRoute>
        <ModuleGuard moduleKey="attendance">
          <AddAttendance />
        </ModuleGuard>
      </PrivateRoute>
    ),
  },
  {
    path: '/attendances/rotation',
    element: (
      <PrivateRoute>
        <ModuleGuard moduleKey="attendance">
          <RotationChecklist />
        </ModuleGuard>
      </PrivateRoute>
    ),
  },
  {
    path: '/fuel',
    element: (
      <PrivateRoute>
        <ModuleGuard moduleKey="fuel">
          <Fuel />
        </ModuleGuard>
      </PrivateRoute>
    ),
  },
  {
    path: '/fuel/add',
    element: (
      <PrivateRoute>
        <ModuleGuard moduleKey="fuel">
          <AddFuel />
        </ModuleGuard>
      </PrivateRoute>
    ),
  },
  {
    path: '/fuel/transfer',
    element: (
      <ModuleGuard moduleKey="fuel">
        <TransferFuel />
      </ModuleGuard>
    ),
  },
  {
    path: '/maintenances',
    element: (
      <PrivateRoute>
        <ModuleGuard moduleKey="maintenance">
          <Maintenance />
        </ModuleGuard>
      </PrivateRoute>
    ),
  },
  {
    path: '/maintenances/add',
    element: (
      <PrivateRoute>
        <ModuleGuard moduleKey="maintenance">
          <AddMaintenance />
        </ModuleGuard>
      </PrivateRoute>
    ),
  },
  {
    path: '/maintenances/:maintenanceId',
    element: (
      <PrivateRoute>
        <ModuleGuard moduleKey="maintenance">
          <MaintenanceDetails />
        </ModuleGuard>
      </PrivateRoute>
    ),
  },
  {
    path: '/tasks',
    element: (
      <PrivateRoute>
        <Task />
      </PrivateRoute>
    ),
  },
  {
    path: '/tasks/create',
    element: (
      <PrivateRoute>
        <CreateTask />
      </PrivateRoute>
    ),
  },
  {
    path: '/cashflow',
    element: (
      <PrivateRoute>
        <ModuleGuard moduleKey="cashflow">
          <CashflowHome />
        </ModuleGuard>
      </PrivateRoute>
    ),
  },
  {
    path: '/cashflow/expense',
    element: (
      <PrivateRoute>
        <ModuleGuard moduleKey="cashflow">
          <AddExpense />
        </ModuleGuard>
      </PrivateRoute>
    ),
  },
  {
    path: '/cashflow/expense/:expenseId',
    element: (
      <PrivateRoute>
        <ModuleGuard moduleKey="cashflow">
          <AddExpense />
        </ModuleGuard>
      </PrivateRoute>
    ),
  },
  {
    path: '/cashflow/return',
    element: (
      <PrivateRoute>
        <ModuleGuard moduleKey="cashflow">
          <ReturnFund />
        </ModuleGuard>
      </PrivateRoute>
    ),
  },
  {
    path: '/cashflow/expenses',
    element: (
      <PrivateRoute>
        <ModuleGuard moduleKey="cashflow">
          <CashflowExpenses />
        </ModuleGuard>
      </PrivateRoute>
    ),
  },
  {
    path: '/cashflow/returns',
    element: (
      <PrivateRoute>
        <ModuleGuard moduleKey="cashflow">
          <CashflowReturns />
        </ModuleGuard>
      </PrivateRoute>
    ),
  },
]

export default MainRoutes

// export const filteredMainRoutes = isAuthCookiePresent()
//   ? MainRoutes
//   : MainRoutes.filter(
//       (route) => !route.path || !route.path.startsWith('/dashboard')
//     )
